import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, 
  ChevronDown, 
  ChevronUp, 
  Image, 
  Infinity as InfinityIcon, 
  Clock, 
  UserPlus, 
  Download, 
  ShieldCheck, 
  CalendarDays, 
  CreditCard,
  Link as LinkIcon,
  Share2,
  X
} from 'lucide-react';

interface PricingPlan {
  name: string;
  description: string;
  price: number;
  credits: number;
  features: { text: string; available: boolean }[];
  highlightFeature?: string;
  buttonText: string;
  buttonLink: string;
  isPopular?: boolean;
}

const Pricing = () => {
  const [selectedFaq, setSelectedFaq] = useState<string | null>(null);

  const plans: PricingPlan[] = [
    {
      name: 'Basic',
      description: 'Perfect for small gatherings and family events',
      price: 19.99,
      credits: 100,
      highlightFeature: '100 photo uploads',
      features: [
        { text: '100 image credits', available: true },
        { text: 'Unlimited events', available: true },
        { text: '3 months event hosting', available: true },
        { text: 'QR code for easy sharing', available: true },
        { text: 'Guest photo uploads', available: true },
        { text: 'Customizable gallery', available: false },
        { text: 'Bulk download', available: false },
        { text: 'Priority support', available: false }
      ],
      buttonText: 'Get Started',
      buttonLink: '/register'
    },
    {
      name: 'Standard',
      description: 'Ideal for weddings and larger celebrations',
      price: 39.99,
      credits: 300,
      highlightFeature: '300 photo uploads',
      features: [
        { text: '300 image credits', available: true },
        { text: 'Unlimited events', available: true },
        { text: '3 months event hosting', available: true },
        { text: 'QR code for easy sharing', available: true },
        { text: 'Guest photo uploads', available: true },
        { text: 'Customizable gallery', available: true },
        { text: 'Bulk download', available: false },
        { text: 'Priority support', available: false }
      ],
      buttonText: 'Choose Standard',
      buttonLink: '/register',
      isPopular: true
    },
    {
      name: 'Professional',
      description: 'For photographers and event planners',
      price: 59.99,
      credits: 500,
      highlightFeature: '500 photo uploads',
      features: [
        { text: '500 image credits', available: true },
        { text: 'Unlimited events', available: true },
        { text: '3 months event hosting', available: true },
        { text: 'QR code for easy sharing', available: true },
        { text: 'Guest photo uploads', available: true },
        { text: 'Customizable gallery', available: true },
        { text: 'Bulk download', available: true },
        { text: 'Priority support', available: true }
      ],
      buttonText: 'Choose Professional',
      buttonLink: '/register'
    }
  ];

  const faqs = [
    {
      id: 'how-credits-work',
      question: 'How does the image credit system work?',
      answer: 'Our platform uses a simple credit system where each photo uploaded consumes one image credit. You purchase a package with a set number of credits (100, 300, or 500) and can use these credits across any number of events you create. For example, if you purchase the Standard package with 300 credits, you could upload 150 photos to one event and 150 to another, or any other combination up to 300 total photos.'
    },
    {
      id: 'what-is-credit',
      question: 'What exactly is an image credit?',
      answer: 'An image credit represents one photo upload to our platform. Each time you or your guests upload a photo to any of your events, one credit is deducted from your account. Our credit-based system ensures you only pay for what you use, with no surprise fees or subscriptions.'
    },
    {
      id: 'credits-expire',
      question: 'Do image credits expire?',
      answer: 'No, image credits never expire. Once purchased, they remain in your account indefinitely until you use them. This allows you to purchase credits now and use them for future events whenever you need them.'
    },
    {
      id: 'duration-ends',
      question: 'What happens when my 3-month hosting period ends?',
      answer: 'After the 3-month hosting period ends, your event becomes read-only. This means all photos will still be viewable, but new uploads will be disabled. You can continue to access, view, and download all photos indefinitely. If you need to extend the active hosting period to allow more uploads, please contact our support team.'
    },
    {
      id: 'unlimited-events',
      question: 'What does "unlimited events" really mean?',
      answer: 'You can create as many different event collections as you want with no additional cost. There\'s no limit to the number of events you can create - whether it\'s a wedding, birthday, corporate event, or family reunion. Each event will share from your pool of available image credits.'
    },
    {
      id: 'guest-uploads',
      question: 'How do guest uploads work?',
      answer: 'You can enable guest uploads for any event, allowing your attendees to contribute their own photos. Each guest upload still uses one of your image credits. You can disable guest uploads at any time from your event settings if you want to reserve your remaining credits for your own uploads only.'
    },
    {
      id: 'additional-credits',
      question: 'What if I need more image credits?',
      answer: 'You can purchase additional image credits at any time from your dashboard. There\'s no need to upgrade your plan - just buy more credits as needed. We offer the same packages (100, 300, or 500 credits) for additional purchases.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Hero Section with Animated Gradient Background */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#fff9f9] to-[#fef4f4] z-0"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-block px-3 py-1 bg-[#FFEEEE] text-[#FF4444] font-medium text-sm mb-6 rounded-full">
            TRANSPARENT CREDIT-BASED PRICING
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#FF4444] to-[#FF8080]">
            Simple pricing for all your photo needs
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8">
            No subscriptions, no hidden fees. Just pay for the photos you upload and create as many events as you need.
          </p>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`
                  relative bg-white rounded-xl transition-all
                  ${plan.isPopular ? 'border-2 border-[#FF4444]' : 'border border-gray-200'}
                `}
              >
                {plan.isPopular && (
                  <div className="absolute -top-4 left-0 right-0 mx-auto w-32 bg-gradient-to-r from-[#FF4444] to-[#FF8080] text-white text-xs font-medium text-center py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}

                <div className={`px-6 ${plan.isPopular ? 'pt-8 pb-6' : 'pt-6 pb-6'}`}>
                  <h3 className="text-xl font-bold mb-1 text-gray-900">{plan.name}</h3>
                  <p className="text-gray-500 text-sm mb-5">{plan.description}</p>

                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-gray-500 text-sm">one-time</span>
                  </div>
                  
                  <div className="bg-[#FFEEEE] rounded-lg p-3 mb-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Image className="w-5 h-5 text-[#FF4444]" />
                        <span className="text-sm font-medium">{plan.credits} image credits</span>
                      </div>
                      <span className="text-xs bg-white px-2 py-1 rounded-full text-[#FF4444] font-medium">
                        ${(plan.price / plan.credits).toFixed(2)}/photo
                      </span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="mb-3 text-sm text-gray-700 font-medium">Includes:</div>
                    <ul className="space-y-3">
                      {plan.features.filter(f => f.available).slice(0, 4).map((feature, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm">
                          <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{feature.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="px-6 py-5 border-t border-gray-100">
                  <Link
                    to={plan.buttonLink}
                    className={`
                      w-full py-3 px-4 rounded-lg text-center font-medium transition-colors block
                      ${plan.isPopular
                        ? 'bg-gradient-to-r from-[#FF4444] to-[#FF6666] text-white hover:from-[#FF3333] hover:to-[#FF5555]'
                        : 'border border-gray-200 text-gray-900 hover:bg-gray-50'
                      }
                    `}
                  >
                    {plan.buttonText}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold mb-4">How Our Pricing Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We've simplified photo sharing with a straightforward credit system that gives you complete flexibility.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <CreditCard className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">1. Choose a Package</h3>
              <p className="text-sm text-gray-600">
                Select a credit package based on how many photos you'll need.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <CalendarDays className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">2. Create Events</h3>
              <p className="text-sm text-gray-600">
                Make as many events as you need - weddings, birthdays, or gatherings.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Image className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">3. Upload Photos</h3>
              <p className="text-sm text-gray-600">
                Each photo uploaded uses one credit from your balance.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-100">
              <div className="w-12 h-12 bg-[#FFEEEE] rounded-lg flex items-center justify-center mb-4">
                <Share2 className="w-6 h-6 text-[#FF4444]" />
              </div>
              <h3 className="font-semibold mb-2">4. Share & Enjoy</h3>
              <p className="text-sm text-gray-600">
                Share your albums with guests and let them contribute photos too.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Key benefits of our pricing model</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We've designed our pricing to give you maximum flexibility and value.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="text-center p-6 rounded-xl border border-gray-100 bg-white">
              <div className="w-16 h-16 bg-[#FFEEEE] rounded-full flex items-center justify-center mx-auto mb-4">
                <InfinityIcon className="w-8 h-8 text-[#FF4444]" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Unlimited Events</h3>
              <p className="text-gray-600 text-sm">
                Create as many events as you need without extra cost. Organize photos for every occasion separately.
              </p>
            </div>

            <div className="text-center p-6 rounded-xl border border-gray-100 bg-white">
              <div className="w-16 h-16 bg-[#FFEEEE] rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-[#FF4444]" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Pay Per Photo</h3>
              <p className="text-gray-600 text-sm">
                Only pay for what you upload. Credits never expire, so you can use them whenever you need them.
              </p>
            </div>

            <div className="text-center p-6 rounded-xl border border-gray-100 bg-white">
              <div className="w-16 h-16 bg-[#FFEEEE] rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="w-8 h-8 text-[#FF4444]" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Collaborative Albums</h3>
              <p className="text-gray-600 text-sm">
                Let event attendees contribute their own photos, creating richer, more complete memories.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-4 text-gray-900">Frequently Asked Questions</h2>
            <p className="text-gray-600">
              Everything you need to know about our pricing and features
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setSelectedFaq(selectedFaq === faq.id ? null : faq.id)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  <div className={`rounded-full p-1 transition-colors ${selectedFaq === faq.id ? 'bg-[#FFEEEE]' : 'bg-gray-100'}`}>
                    {selectedFaq === faq.id ? 
                      <ChevronUp className={`w-4 h-4 ${selectedFaq === faq.id ? 'text-[#FF4444]' : 'text-gray-400'}`} /> : 
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    }
                  </div>
                </button>
                {selectedFaq === faq.id && (
                  <div className="px-6 py-4 border-t border-gray-100 text-sm bg-gray-50">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FF4444] to-[#FF6666] text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to start sharing memories?</h2>
          <p className="mb-8 opacity-90">
            Choose a plan that works for you and start creating your photo collections today.
          </p>
          <Link
            to="/register"
            className="inline-block py-3 px-8 bg-white text-[#FF4444] font-medium rounded-lg shadow-lg hover:shadow-xl transition-all"
          >
            Get Started Now
          </Link>
        </div>
      </section>

      {/* Guarantee Note */}
      <section className="py-10 px-4 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex justify-center items-center mb-2">
            <div className="w-10 h-10 rounded-full border-2 border-[#FF4444] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#FF4444]" />
            </div>
          </div>
          <h3 className="text-lg font-semibold mb-2">Image Credits Never Expire</h3>
          <p className="text-gray-500 text-sm">
            Purchase with confidence - your credits will be available whenever you need them, with no time limits.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Pricing;